import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { rackBinId } from './wms-inbound.seed'
import { machineId, materialId, purchaseUnitOf, smallPerBigOf, warehouseRmId } from './wms-master.seed'
import {
  REQ_ACTION_STATUS,
  type ReqActionName,
  type ReqActorCode,
  type ReqStatusCode,
  reqActionId,
  reqActorId,
  reqStatusId,
  WF_REQ_01_ID
} from './workflow-req.seed'

/**
 * Request Production RM (C06) — ERD v010: mt_product, mt_bill_of_material, material_request(+_line),
 * picking_task(+_item), handover, warehouse_zone (area serah terima produksi).
 * Sumber angka: seed prototype `jal-v7.44` (`master.skus_fg`, `master.bom`, `prod.requests`, `rm.picking`).
 * [A] Unit stok awal prototype (QR-000101 …) diganti unit local-db yang sudah ada (`wms-master.seed.ts`); sisa WIP
 * ETK-XTRA20 2 Box dan unit historis (nol saldo, Delivered) ditambahkan agar usulan WIP-first FIFO, rekap
 * pengambilan, dan serah terima punya contoh data. Request SPK 42-08 (kekurangan stok) dipindah dari
 * `wms-procurement.seed.ts` ke sini supaya semua request satu sumber.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()

const audit = (userId: string, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

const RM_STORAGE_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 1)
/**
 * Area Staging Produksi SPK (v7.50 Q-03 / Q-05): `AREA-STG-PRD-1` "Staging Produksi 1" (kode lama `AREA-STG-PRD`; data lama
 * dipetakan ke sini), `AREA-STG-PRD-2` untuk demo pilihan area, dan area per mesin (`AREA-MOLLINS-1` …).
 */
export const PROD_STAGING_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 10)
export const PROD_STAGING_ZONE_2_ID = seedId(SEED_GROUP.warehouseZone, 17)
export const STAGING_AREAS = {
  1: { id: PROD_STAGING_ZONE_ID, code: 'AREA-STG-PRD-1', name: 'Staging Produksi 1' },
  2: { id: PROD_STAGING_ZONE_2_ID, code: 'AREA-STG-PRD-2', name: 'Staging Produksi 2' }
} as const

const MACHINE_AREAS = [
  ['MOLLINS-1', 'Area Mollins 1'],
  ['MOLLINS-2', 'Area Mollins 2'],
  ['PROTOS-1', 'Area Protos 1'],
  ['PROTOS-2', 'Area Protos 2'],
  ['SASIB', 'Area Sasib'],
  ['PRO-MILD', 'Area Pro Mild']
] as const

type MachineCode = (typeof MACHINE_AREAS)[number][0]
const areaZoneId = (machine: MachineCode) =>
  seedId(SEED_GROUP.warehouseZone, 11 + MACHINE_AREAS.findIndex(([code]) => code === machine))

/* ------------------------------------------------------------ master SKU & BOM */

/** [kode, nama, tipe, btg per pack, ada BOM?]. Tobaco One Filter 16 sengaja tanpa BOM (prototype: "tanpa BOM — manual"). */
const SKUS = [
  ['TBC-ONE-12', 'Tobaco One 12', 'SKM', 12, true],
  ['TBC-ONE-F16', 'Tobaco One Filter 16', 'SKM', 16, false],
  ['TBC-BOLD-20', 'Tabaco Bold 20', 'SKM', 20, true],
  ['TBC-BOLD-20-SKT', 'Tabaco Bold 20 SKT', 'SKT', 20, true],
  ['TBC-XTRA-20', 'Tabaco Xtra 20', 'SKM', 20, true]
] as const

type SkuCode = (typeof SKUS)[number][0]
export const skuId = (code: SkuCode) => seedId(SEED_GROUP.sku, SKUS.findIndex(([c]) => c === code) + 1)

const BOM: Record<string, string[]> = {
  'TBC-BOLD-20': [
    'ETK-BOLD20',
    'PPR-REG',
    'FLT-B06',
    'CTP-BOLD',
    'FOIL-BLK86',
    'INN-BLK96',
    'OPP-125',
    'OPP-PRS300',
    'TT-GLDH',
    'KRF-75',
    'TSG-REG',
    'LEM-C'
  ],
  'TBC-BOLD-20-SKT': [
    'ETK-BOLD20',
    'PPR-REG',
    'FOIL-BLK86',
    'INN-BLK96',
    'OPP-125',
    'TT-GLDH',
    'KRF-75',
    'TSG-REG',
    'LEM-C'
  ],
  'TBC-ONE-12': [
    'ETK-ONE12',
    'PPR-REG',
    'FLT-SF13',
    'FOIL-GLD70',
    'INN-GLD79',
    'OPP-125',
    'TT-GLDH',
    'KRF-75',
    'TSG-REG',
    'LEM-C'
  ],
  'TBC-XTRA-20': [
    'ETK-XTRA20',
    'PPR-REG',
    'FLT-B06',
    'FOIL-BLK86',
    'INN-BLK96',
    'OPP-125',
    'TT-GLDH',
    'KRF-75',
    'TSG-MLD',
    'LEM-C'
  ]
}

/* ------------------------------------------------------------ unit stok yang direferensikan tugas */

/** Unit stok awal (`wms-master.seed.ts` urutan STOCK_IN) dan unit tembakau terima (`wms-inbound.seed.ts`). */
const UNIT = {
  'ETK-BOLD20': seedId(SEED_GROUP.rmUnit, 2),
  'FLT-B06': seedId(SEED_GROUP.rmUnit, 3),
  'PPR-REG': seedId(SEED_GROUP.rmUnit, 4),
  'ETK-XTRA20': seedId(SEED_GROUP.rmUnit, 5),
  'ETK-XTRA20-WIP': seedId(SEED_GROUP.rmUnit, 400),
  'FLT-B06-H': seedId(SEED_GROUP.rmUnit, 401),
  'INN-BLK96-H': seedId(SEED_GROUP.rmUnit, 402),
  'PPR-REG-H': seedId(SEED_GROUP.rmUnit, 403),
  'TSG-REG-H': seedId(SEED_GROUP.rmUnit, 404)
} as const

type UnitKey = keyof typeof UNIT

/** Rak asal unit (kode slot) untuk `from_rack_bin_id` pada item tugas. */
const UNIT_RACK: Record<UnitKey, string> = {
  'ETK-BOLD20': 'R03-B01-L01-P01',
  'FLT-B06': 'R03-B01-L02-P01',
  'PPR-REG': 'R03-B02-L01-P01',
  'ETK-XTRA20': 'R03-B02-L02-P01',
  'ETK-XTRA20-WIP': 'R03-B02-L02-P01',
  'FLT-B06-H': 'R03-B01-L02-P01',
  'INN-BLK96-H': 'R03-B01-L02-P01',
  'PPR-REG-H': 'R03-B02-L01-P01',
  'TSG-REG-H': 'R01-B04-L01-P01'
}

/** Unit tambahan: sisa WIP (stok aktif) + unit historis (saldo 0, Delivered). */
const EXTRA_UNITS: [UnitKey, string, string, string, number, boolean, string, string][] = [
  // [key, material, qr, batch, qty_big, wip, status, createdAt]
  [
    'ETK-XTRA20-WIP',
    'ETK-XTRA20',
    'ETK-XTRA20:20260805:01:0001',
    'ETK-XTRA20-260805-01',
    2,
    true,
    'IN_STOCK',
    '2026-08-05 15:00'
  ],
  ['FLT-B06-H', 'FLT-B06', 'FLT-B06:20260805:01:0002', 'FLT-B06-260805-01', 0, false, 'DELIVERED', '2026-08-05 15:00'],
  [
    'INN-BLK96-H',
    'INN-BLK96',
    'INN-BLK96:20260805:01:0001',
    'INN-BLK96-260805-01',
    0,
    false,
    'DELIVERED',
    '2026-08-05 15:00'
  ],
  ['PPR-REG-H', 'PPR-REG', 'PPR-REG:20260810:01:0002', 'PPR-REG-260810-01', 0, false, 'DELIVERED', '2026-08-10 09:00'],
  ['TSG-REG-H', 'TSG-REG', 'TSG-REG:20260810:01:0001', 'TSG-REG-260810-01', 0, false, 'DELIVERED', '2026-08-10 09:00']
]

const BIG_UNIT_NAME: Record<string, string> = {
  'ETK-XTRA20': 'Box',
  'FLT-B06': 'Dos',
  'INN-BLK96': 'Roll',
  'PPR-REG': 'Roll',
  'TSG-REG': 'Sak'
}

/* ------------------------------------------------------------ request, tugas, serah terima */

type ReqLine = { mat: string; qty: number; note?: string; machine?: MachineCode; shortage?: number }

type ReqStep = [action: ReqActionName, actor: ReqActorCode, by: string, at: string]

type ReqSeed = {
  no: string
  type: 'SPK' | 'TOBACCO'
  spk?: string
  sku?: SkuCode
  prodQty?: number
  at: string
  status: ReqStatusCode
  lines: ReqLine[]
  steps: ReqStep[]
  /** Area Staging Produksi (1 | 2) — hanya SPK; bawaan 1 (pemetaan data lama). */
  stg?: 1 | 2
}

const SEND = (at: string): ReqStep => ['ACT_REQ_SEND', 'ACT_REQ_PRODUKSI', 'joko', at]
const ASSIGN = (at: string): ReqStep => ['ACT_REQ_ASSIGN', 'ACT_REQ_ADMIN_RM', 'yulia', at]
const PICK_DONE = (at: string, by = 'karno'): ReqStep => ['ACT_REQ_PICK_DONE', 'ACT_REQ_OP_RM', by, at]
const ACCEPT = (at: string): ReqStep => ['ACT_REQ_ACCEPT', 'ACT_REQ_PRODUKSI', 'joko', at]

const REQUESTS: ReqSeed[] = [
  {
    no: 'REQ-260818-004',
    type: 'SPK',
    spk: '42-08/JLC/26',
    sku: 'TBC-BOLD-20',
    prodQty: 60000,
    at: '2026-08-18 09:40',
    status: 'REQ_PENDING_ASSIGN',
    lines: [
      { mat: 'ETK-BOLD12', qty: 3, note: 'Etiket untuk SPK 42-08', shortage: 3 },
      { mat: 'PPR-MILD', qty: 4, shortage: 4 },
      { mat: 'FLT-B06', qty: 18, note: 'Sebagian tercukupi stok', shortage: 3 }
    ],
    steps: [['ACT_REQ_SHORTAGE_PR', 'ACT_REQ_PRODUKSI', 'joko', '2026-08-18 09:41']]
  },
  {
    no: 'REQ-260816-001',
    type: 'SPK',
    spk: '15-10/JLC/20',
    sku: 'TBC-BOLD-20',
    prodQty: 2080000,
    at: '2026-08-16 14:00',
    status: 'REQ_COMPLETED',
    lines: [{ mat: 'PPR-REG', qty: 6 }],
    steps: [
      SEND('2026-08-16 14:02'),
      ASSIGN('2026-08-16 15:00'),
      PICK_DONE('2026-08-18 05:55'),
      ACCEPT('2026-08-18 06:20')
    ]
  },
  {
    no: 'REQ-260817-001',
    type: 'SPK',
    spk: '39-08/JLC/26',
    sku: 'TBC-ONE-F16',
    prodQty: 50000,
    at: '2026-08-17 08:00',
    status: 'REQ_COMPLETED',
    lines: [
      { mat: 'FLT-B06', qty: 5 },
      { mat: 'INN-BLK96', qty: 3 }
    ],
    steps: [
      SEND('2026-08-17 08:02'),
      ASSIGN('2026-08-17 08:40'),
      PICK_DONE('2026-08-18 06:35', 'tono'),
      ACCEPT('2026-08-18 06:55')
    ]
  },
  {
    no: 'REQ-260817-002',
    type: 'TOBACCO',
    at: '2026-08-17 06:50',
    status: 'REQ_COMPLETED',
    lines: [{ mat: 'TSG-REG', qty: 90, note: 'Shift pagi', machine: 'MOLLINS-1' }],
    steps: [
      SEND('2026-08-17 06:51'),
      ASSIGN('2026-08-17 07:00'),
      PICK_DONE('2026-08-17 07:15'),
      ACCEPT('2026-08-17 07:20')
    ]
  },
  {
    no: 'REQ-260817-003',
    type: 'TOBACCO',
    at: '2026-08-17 06:52',
    status: 'REQ_COMPLETED',
    lines: [{ mat: 'TSG-REG', qty: 70, machine: 'MOLLINS-2' }],
    steps: [
      SEND('2026-08-17 06:53'),
      ASSIGN('2026-08-17 07:02'),
      PICK_DONE('2026-08-17 07:20'),
      ACCEPT('2026-08-17 07:25')
    ]
  },
  {
    no: 'REQ-260817-004',
    type: 'TOBACCO',
    at: '2026-08-17 06:55',
    status: 'REQ_COMPLETED',
    lines: [{ mat: 'TSG-REG', qty: 60, machine: 'SASIB' }],
    steps: [
      SEND('2026-08-17 06:56'),
      ASSIGN('2026-08-17 07:04'),
      PICK_DONE('2026-08-17 07:25'),
      ACCEPT('2026-08-17 07:30')
    ]
  },
  {
    no: 'REQ-260818-001',
    type: 'SPK',
    spk: '40-08/JLC/26',
    sku: 'TBC-ONE-12',
    prodQty: 60000,
    stg: 2,
    at: '2026-08-18 07:30',
    status: 'REQ_PICKING_ASSIGNED',
    lines: [
      { mat: 'ETK-BOLD20', qty: 10, note: 'Utk SPK 40-08' },
      { mat: 'ETK-XTRA20', qty: 10 }
    ],
    steps: [SEND('2026-08-18 07:31'), ASSIGN('2026-08-18 08:05')]
  },
  {
    no: 'REQ-260818-002',
    type: 'SPK',
    spk: '41-08/JLC/26',
    sku: 'TBC-ONE-F16',
    prodQty: 40000,
    at: '2026-08-18 09:05',
    status: 'REQ_PENDING_ASSIGN',
    lines: [
      { mat: 'FLT-B06', qty: 15, note: 'Utk SPK 41-08' },
      { mat: 'PPR-REG', qty: 20 }
    ],
    steps: [SEND('2026-08-18 09:06')]
  },
  {
    no: 'REQ-260818-003',
    type: 'TOBACCO',
    at: '2026-08-18 06:40',
    status: 'REQ_PENDING_ASSIGN',
    lines: [{ mat: 'TSG-REG', qty: 12, note: 'Shift pagi', machine: 'MOLLINS-2' }],
    steps: [SEND('2026-08-18 06:41')]
  },
  {
    no: 'REQ-260818-009',
    type: 'TOBACCO',
    at: '2026-08-18 11:05',
    status: 'REQ_PENDING_ASSIGN',
    lines: [
      { mat: 'TSG-MLD', qty: 6, note: 'Shift pagi', machine: 'MOLLINS-1' },
      { mat: 'TSG-MLD', qty: 4, machine: 'MOLLINS-2' },
      { mat: 'TSG-REG', qty: 3, note: 'Campuran', machine: 'MOLLINS-2' }
    ],
    steps: [SEND('2026-08-18 11:06')]
  }
]

/** Kode Area Staging Produksi request SPK (tugas picking SPK dan serah terimanya menuju area ini). */
const stagingCodeOf = (reqNo: string) => {
  const request = REQUESTS.find(item => item.no === reqNo)
  return request?.type === 'SPK' ? STAGING_AREAS[request.stg ?? 1].code : null
}

type TaskItem = { unit: UnitKey; line: number; plan: number; taken?: number; status: 'PENDING' | 'PICKED'; at?: string }

type TaskSeed = {
  no: string
  req: string
  op: string
  status: 'ASSIGNED' | 'COMPLETED'
  at: string
  machine?: MachineCode
  items: TaskItem[]
  handover?: string
}

const TASKS: TaskSeed[] = [
  {
    no: 'PICK-260817-001',
    req: 'REQ-260817-002',
    op: 'karno',
    status: 'COMPLETED',
    at: '2026-08-17 07:00',
    machine: 'MOLLINS-1',
    handover: 'HND-260817-001',
    items: [{ unit: 'TSG-REG-H', line: 0, plan: 90, taken: 90, status: 'PICKED', at: '2026-08-17 07:15' }]
  },
  {
    no: 'PICK-260817-002',
    req: 'REQ-260817-003',
    op: 'karno',
    status: 'COMPLETED',
    at: '2026-08-17 07:02',
    machine: 'MOLLINS-2',
    handover: 'HND-260817-002',
    items: [{ unit: 'TSG-REG-H', line: 0, plan: 70, taken: 70, status: 'PICKED', at: '2026-08-17 07:20' }]
  },
  {
    no: 'PICK-260817-003',
    req: 'REQ-260817-004',
    op: 'karno',
    status: 'COMPLETED',
    at: '2026-08-17 07:04',
    machine: 'SASIB',
    handover: 'HND-260817-003',
    items: [{ unit: 'TSG-REG-H', line: 0, plan: 60, taken: 60, status: 'PICKED', at: '2026-08-17 07:25' }]
  },
  {
    no: 'PICK-260817-004',
    req: 'REQ-260817-001',
    op: 'tono',
    status: 'COMPLETED',
    at: '2026-08-17 08:40',
    handover: 'HND-260818-001',
    items: [
      { unit: 'FLT-B06-H', line: 0, plan: 5, taken: 5, status: 'PICKED', at: '2026-08-18 06:20' },
      { unit: 'INN-BLK96-H', line: 1, plan: 3, taken: 3, status: 'PICKED', at: '2026-08-18 06:30' }
    ]
  },
  {
    no: 'PICK-260817-005',
    req: 'REQ-260816-001',
    op: 'karno',
    status: 'COMPLETED',
    at: '2026-08-16 15:00',
    handover: 'HND-260818-002',
    items: [{ unit: 'PPR-REG-H', line: 0, plan: 6, taken: 6, status: 'PICKED', at: '2026-08-18 05:50' }]
  },
  {
    no: 'PICK-260818-001',
    req: 'REQ-260818-001',
    op: 'karno',
    status: 'ASSIGNED',
    at: '2026-08-18 08:05',
    items: [{ unit: 'ETK-BOLD20', line: 0, plan: 10, status: 'PENDING' }]
  },
  {
    no: 'PICK-260818-002',
    req: 'REQ-260818-001',
    op: 'tono',
    status: 'ASSIGNED',
    at: '2026-08-18 08:05',
    items: [
      { unit: 'ETK-XTRA20-WIP', line: 1, plan: 2, status: 'PENDING' },
      { unit: 'ETK-XTRA20', line: 1, plan: 8, status: 'PENDING' }
    ]
  }
]

/** [handover, request, machine/area, diserahkan pada]. Semua historis berstatus Delivered. */
const HANDOVERS: [string, string, MachineCode | null, string][] = [
  ['HND-260817-001', 'REQ-260817-002', 'MOLLINS-1', '2026-08-17 07:20'],
  ['HND-260817-002', 'REQ-260817-003', 'MOLLINS-2', '2026-08-17 07:25'],
  ['HND-260817-003', 'REQ-260817-004', 'SASIB', '2026-08-17 07:30'],
  ['HND-260818-001', 'REQ-260817-001', null, '2026-08-18 06:55'],
  ['HND-260818-002', 'REQ-260816-001', null, '2026-08-18 06:20']
]

/* ------------------------------------------------------------ build */

export function buildWmsRequestSeed(): SeedTables {
  const admin = USER_IDS.yulia
  const warehouse_zone: LocalRow[] = [
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: PROD_STAGING_ZONE_ID,
      code: STAGING_AREAS[1].code,
      name: STAGING_AREAS[1].name,
      warehouse_id: warehouseRmId,
      zone_type: 'PRODUCTION_AREA',
      machine_id: null
    },
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: PROD_STAGING_ZONE_2_ID,
      code: STAGING_AREAS[2].code,
      name: STAGING_AREAS[2].name,
      warehouse_id: warehouseRmId,
      zone_type: 'PRODUCTION_AREA',
      machine_id: null
    },
    ...MACHINE_AREAS.map(([machine, name]) => ({
      ...audit(admin, '2026-08-01 07:00'),
      id: areaZoneId(machine),
      code: `AREA-${machine}`,
      name,
      warehouse_id: warehouseRmId,
      zone_type: 'PRODUCTION_AREA',
      machine_id: machineId(machine)
    }))
  ]

  const mt_product: LocalRow[] = SKUS.map(([code, name, type, btg]) => ({
    ...audit(admin, '2026-08-01 07:00'),
    id: skuId(code),
    code,
    name,
    type,
    hierarchy: 'Karton>Bal>Slop>Pack',
    pack_per_slop: 10,
    slop_per_bal: 20,
    bal_per_karton: 4,
    btg_per_pack: btg,
    has_bal: true,
    is_active: true
  }))

  let bomIndex = 0
  const mt_bill_of_material: LocalRow[] = Object.entries(BOM).flatMap(([sku, materials]) =>
    materials.map(material => ({
      ...audit(admin, '2026-08-01 07:00'),
      id: seedId(SEED_GROUP.billOfMaterial, ++bomIndex),
      sku_id: skuId(sku as SkuCode),
      material_id: materialId(material),
      qty_per_unit: 1,
      unit_id: purchaseUnitOf(material)
    }))
  )

  const rm_unit: LocalRow[] = EXTRA_UNITS.map(([key, material, qr, batch, qtyBig, wip, unitStatus, at], index) => ({
    ...audit(admin, at),
    id: UNIT[key],
    uid: `UNIT-${String(400 + index).padStart(6, '0')}`,
    qr_code: qr,
    material_id: materialId(material),
    batch_no: batch,
    supplier_id: null,
    po_no: null,
    qty_small: qtyBig * smallPerBigOf(material),
    rack_bin_id: unitStatus === 'IN_STOCK' ? rackBinId(UNIT_RACK[key]) : null,
    warehouse_zone_id: unitStatus === 'IN_STOCK' ? RM_STORAGE_ZONE_ID : null,
    flag: wip ? 'WIP' : 'REGULAR',
    unit_status: unitStatus,
    qc_date: utc(at),
    unit_big_snapshot: BIG_UNIT_NAME[material] ?? null,
    small_per_big_snapshot: smallPerBigOf(material),
    is_leftover_wip: wip,
    stored_at: utc(at)
  }))

  const material_request: LocalRow[] = []
  const material_request_line: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  const requestIdByNo = new Map<string, string>()
  const lineIdsByNo = new Map<string, string[]>()

  REQUESTS.forEach((request, index) => {
    const requestId = seedId(SEED_GROUP.materialRequest, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 500 + index + 1)
    requestIdByNo.set(request.no, requestId)
    const last = request.steps[request.steps.length - 1]
    const shortage = request.lines.some(line => (line.shortage ?? 0) > 0)
    material_request.push({
      ...audit(USER_IDS.joko, request.at),
      id: requestId,
      req_no: request.no,
      type: request.type,
      spk_no: request.spk ?? null,
      sku_id: request.sku ? skuId(request.sku) : null,
      prod_qty: request.prodQty ?? null,
      prod_unit_id: null,
      staging_zone_id: request.type === 'SPK' ? STAGING_AREAS[request.stg ?? 1].id : null,
      has_shortage: shortage,
      status_id: reqStatusId(request.status),
      submission_id: submissionId
    })
    const lineIds: string[] = []
    request.lines.forEach((line, lineIndex) => {
      const lineId = seedId(SEED_GROUP.materialRequestLine, (index + 1) * 10 + lineIndex)
      lineIds.push(lineId)
      material_request_line.push({
        ...audit(USER_IDS.joko, request.at),
        id: lineId,
        request_id: requestId,
        material_id: materialId(line.mat),
        machine_id: line.machine ? machineId(line.machine) : null,
        qty: line.qty,
        unit_id: purchaseUnitOf(line.mat),
        note: line.note ?? '',
        settled_qty: request.status === 'REQ_COMPLETED' ? line.qty - (line.shortage ?? 0) : 0,
        is_shortage: (line.shortage ?? 0) > 0,
        shortage_qty: line.shortage ?? 0
      })
    })
    lineIdsByNo.set(request.no, lineIds)
    wxl_submission.push({
      ...audit(USER_IDS.joko, request.at),
      id: submissionId,
      workflow_id: WF_REQ_01_ID,
      entity: 'material_request',
      entity_id: requestId,
      source: 'ACTION',
      current_status_id: reqStatusId(request.status),
      last_action_id: reqActionId(last[0]),
      workflow_snapshot: null
    })
    request.steps.forEach(([action, actor, by, at], step) => {
      const [from, to] = REQ_ACTION_STATUS[action]
      wxl_submission_log.push({
        ...audit(USER_IDS[by], at),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 5000 + (index + 1) * 10 + step),
        workflow_id: WF_REQ_01_ID,
        submission_id: submissionId,
        action_id: reqActionId(action),
        actor_id: reqActorId(actor),
        from_status_id: reqStatusId(from),
        to_status_id: reqStatusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: null,
        entity: 'material_request',
        entity_id: requestId
      })
    })
  })

  const handoverIdByNo = new Map(HANDOVERS.map(([no], index) => [no, seedId(SEED_GROUP.handover, index + 1)]))
  const handover: LocalRow[] = HANDOVERS.map(([no, req, machine, at]) => ({
    ...audit(USER_IDS.karno, at),
    id: handoverIdByNo.get(no) as string,
    handover_no: no,
    request_id: requestIdByNo.get(req),
    machine_id: machine ? machineId(machine) : null,
    area_code: machine ? `AREA-${machine}` : stagingCodeOf(req),
    handover_status: 'DELIVERED',
    delivered_at: utc(at),
    received_by: USER_IDS.joko,
    reject_reason: null
  }))

  const picking_task: LocalRow[] = []
  const picking_task_item: LocalRow[] = []
  TASKS.forEach((task, index) => {
    const taskId = seedId(SEED_GROUP.pickingTask, index + 1)
    picking_task.push({
      ...audit(USER_IDS.yulia, task.at),
      id: taskId,
      pick_no: task.no,
      type: 'REQUEST',
      doc_ref: task.req,
      operator_id: USER_IDS[task.op],
      task_status: task.status,
      machine_id: task.machine ? machineId(task.machine) : null,
      area_code: task.machine ? `AREA-${task.machine}` : stagingCodeOf(task.req)
    })
    task.items.forEach((item, itemIndex) => {
      const lineId = (lineIdsByNo.get(task.req) as string[])[item.line]
      picking_task_item.push({
        ...audit(USER_IDS[task.op], item.at ?? task.at),
        id: seedId(SEED_GROUP.pickingTaskItem, (index + 1) * 10 + itemIndex),
        task_id: taskId,
        unit_id: UNIT[item.unit],
        line_id: lineId,
        qty_plan: item.plan,
        qty_taken: item.taken ?? 0,
        from_rack_bin_id: rackBinId(UNIT_RACK[item.unit]),
        from_warehouse_zone_id: RM_STORAGE_ZONE_ID,
        is_wip: item.unit === 'ETK-XTRA20-WIP',
        picked_at: item.at ? utc(item.at) : null,
        handover_id: task.handover ? (handoverIdByNo.get(task.handover) ?? null) : null,
        status: item.status
      })
    })
  })

  return {
    warehouse_zone,
    mt_product,
    mt_bill_of_material,
    rm_unit,
    material_request,
    material_request_line,
    wxl_submission,
    wxl_submission_log,
    handover,
    picking_task,
    picking_task_item
  }
}
